.sidebar {
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  height: 100%;
  min-height: 0;
  padding: var(--space-3) var(--space-2);
  gap: var(--space-2);

  /*
   * Two local custom properties rather than two literals further down.
   * `tokens.css` belongs to the design system and is not this component's to
   * extend, and an inline `2px` is how a scale starts leaking.
   *
   * The wordmark height is optical: the file is 360 by 130 with generous
   * internal margin, so it is sized by height and lets the width follow,
   * matching the arc's weight to the type in the topbar rather than to the box
   * the asset was exported in.
   *
   * The active rule is 2 units because a hairline reads as a rendering
   * artefact against the row tint and 3 starts competing with the icon.
   */
  --wordmark-h: 2rem;
  --active-rule-w: 2px;
}

/* In the drawer the border and the elevation belong to the drawer panel. The
   sidebar only has to bring its own surface. */
.sidebar[data-drawer='true'] {
  background: var(--surface-sunken);
}

/* ---- Head ---- */

.head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-2);
  /* Lines the wordmark up with the topbar's baseline across the border. */
  min-height: calc(var(--topbar-h) - var(--space-3));
  padding: 0 var(--space-2);
}

.sidebar[data-collapsed='true'] .head {
  grid-template-columns: minmax(0, 1fr);
  justify-items: center;
}

.brand {
  display: flex;
  align-items: center;
  min-width: 0;
  border-radius: var(--radius-sm);
  text-decoration: none;
}

.wordmarkForLight,
.wordmarkForDark {
  width: auto;
  height: var(--wordmark-h);
}

.mark {
  color: var(--text);
}

/*
 * The tri-state, reproduced. This is the same logic `light-dark()` runs, spelt
 * out because CSS cannot pick an `<img>` source from a colour function.
 *
 *   default              light, so the Ink wordmark
 *   OS dark, unpinned    the Harmattan one
 *   pinned dark          the Harmattan one, whatever the OS says
 *   pinned light         the Ink one, whatever the OS says
 *
 * The last of those is the case a `<picture media>` element gets wrong, and it
 * gets it wrong by rendering a cream logo on a cream page.
 */
.wordmarkForDark {
  display: none;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-scheme='light']) .wordmarkForLight {
    display: none;
  }

  :root:not([data-scheme='light']) .wordmarkForDark {
    display: block;
  }
}

:root[data-scheme='dark'] .wordmarkForLight {
  display: none;
}

:root[data-scheme='dark'] .wordmarkForDark {
  display: block;
}

/* Fainter than a ghost button's default. This is chrome for the chrome, and it
   should not compete for attention with the navigation it sits above. */
.collapse {
  color: var(--text-faint);
}

.sidebar[data-collapsed='true'] .collapse {
  display: none;
}

/* ---- Organization ---- */

.org {
  padding: 0 var(--space-2);
}

.orgMenu {
  display: flex;
  width: 100%;
}

.orgTrigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  width: 100%;
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--surface);
  font: var(--type-ui-strong);
  transition: background-color var(--dur-instant) var(--ease-out);
}

.orgTrigger:hover {
  background: var(--hover);
}

.orgName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orgChevron {
  flex: none;
  color: var(--text-faint);
}

.orgStatic {
  display: flex;
  align-items: center;
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  font: var(--type-ui-strong);
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * Collapsed, neither form fits in a 3.5rem rail. The topbar's breadcrumb still
 * names the organization, so nothing is lost by dropping it here rather than
 * by squeezing an initial into a circle that has to be hovered to be read.
 */
.sidebar[data-collapsed='true'] .org {
  display: none;
}

/* ---- Navigation ---- */

.nav {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 var(--space-1);
}

.group + .group {
  margin-top: var(--space-4);
}

.groupLabel {
  padding: 0 var(--space-2) var(--space-2);
  font: var(--type-label);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--text-faint);
}

/*
 * Collapsed, the words are clipped out of the visual layout and the heading
 * element stays in the accessibility tree. A hairline stands in for it, so the
 * grouping is still visible to somebody who cannot read the labels because
 * they are not there.
 */
.sidebar[data-collapsed='true'] .groupLabel {
  padding: 0;
  margin: 0 var(--space-2) var(--space-2);
  border-top: 1px solid var(--rule-soft);
}

.list {
  display: grid;
  gap: var(--space-1);
}

.link {
  display: grid;
  /* The icon column is `auto`, sized by the glyph itself. Every icon in this
     set is drawn on the same grid at the same size, so the labels line up
     without a width being asserted here that would have to be kept in step
     with `DEFAULT_SIZE` in Icons.tsx. */
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-3);
  min-height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font: var(--type-ui);
  text-decoration: none;
  transition:
    color var(--dur-instant) var(--ease-out),
    background-color var(--dur-instant) var(--ease-out);
}

.link:hover {
  color: var(--text);
  background: var(--hover);
}

/*
 * The current page carries three signals, not one: the tint, the Sahel rule
 * down the leading edge, and `aria-current="page"`. The tint alone is about one
 * step of lightness on this surface, which does not survive a laptop screen
 * seen at an angle in daylight, and daylight is where this product is used.
 */
.active {
  position: relative;
  color: var(--text);
  background: var(--selected);
  font: var(--type-ui-strong);
}

.active::before {
  content: '';
  position: absolute;
  inset: var(--space-1) auto var(--space-1) 0;
  width: var(--active-rule-w);
  border-radius: var(--radius-pill);
  background: var(--selected-rule);
}

.linkIcon {
  display: grid;
  place-items: center;
}

.linkLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * Collapsed, the row is a centred glyph and the badge becomes a dot. The grid
 * drops to one column so the icon lands in the middle of the rail rather than
 * at its left edge with an empty label track beside it.
 */
.sidebar[data-collapsed='true'] .link {
  position: relative;
  grid-template-columns: minmax(0, 1fr);
  justify-items: center;
  padding: 0;
}

.badge {
  min-width: var(--space-5);
  padding: 0 var(--space-1);
  border-radius: var(--radius-pill);
  background: var(--neutral-soft);
  color: var(--text-muted);
  font: var(--type-micro);
  font-variant-numeric: var(--numeric);
  text-align: center;
}

.active .badge {
  background: var(--accent-soft);
  color: var(--accent-text);
}

/*
 * A count with no label beside it is a number with no noun, so collapsed it
 * reduces to a dot: "there is something here". The count itself is still in
 * the link's accessible name, which is where it was legible anyway.
 */
.sidebar[data-collapsed='true'] .badge {
  position: absolute;
  top: var(--space-1);
  right: var(--space-1);
  width: var(--space-2);
  min-width: 0;
  height: var(--space-2);
  padding: 0;
  overflow: hidden;
  color: transparent;
  background: var(--accent);
}

/* ---- Foot ---- */

.foot {
  padding: var(--space-2) var(--space-3) 0;
  border-top: 1px solid var(--rule-soft);
  font: var(--type-micro);
  color: var(--text-faint);
}
